/*
  Naiak - Cabeçalho (NK__HEADER__V1)
  - Mega menu desktop: abre no hover (mouse) ou no clique/Enter; Esc fecha; aria-expanded sincronizado.
  - Busca: painel abaixo do header (usa predictive-search/search-form do Dawn quando disponíveis).
  - Menu mobile: tela cheia com accordions; foco preso enquanto aberto (trapFocus do Dawn).
*/
if (!customElements.get('nk-header')) {
  class NkHeader extends HTMLElement {
    connectedCallback() {
      this.desktopQuery = window.matchMedia('(min-width: 1024px)');
      this.hoverQuery = window.matchMedia('(hover: hover)');
      this.overlay = this.querySelector('[data-nk-overlay]');
      this.dropdowns = Array.from(this.querySelectorAll('[data-nk-dropdown]'));
      this.search = this.querySelector('[data-nk-search]');
      this.searchToggles = Array.from(this.querySelectorAll('[data-nk-search-toggle]'));
      this.drawer = this.querySelector('[data-nk-drawer]');
      this.drawerOpenButton = this.querySelector('[data-nk-drawer-open]');
      this.openItem = null;
      this.closeTimer = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMediaChange = this.closeAll.bind(this);

      this.dropdowns.forEach((item) => this.setupDropdown(item));

      this.searchToggles.forEach((button) =>
        button.addEventListener('click', () => (this.isSearchOpen() ? this.closeSearch(true) : this.openSearch()))
      );
      this.querySelectorAll('[data-nk-search-close]').forEach((button) =>
        button.addEventListener('click', () => this.closeSearch(true))
      );

      if (this.overlay) this.overlay.addEventListener('click', () => this.closeAll());

      if (this.drawerOpenButton) this.drawerOpenButton.addEventListener('click', () => this.openDrawer());
      this.querySelectorAll('[data-nk-drawer-close]').forEach((button) =>
        button.addEventListener('click', () => this.closeDrawer())
      );
      this.querySelectorAll('[data-nk-accordion]').forEach((button) =>
        button.addEventListener('click', () => this.toggleAccordion(button))
      );

      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      this.desktopQuery.addEventListener('change', this.onMediaChange);

      // Editor de temas: selecionar um bloco "Painel do mega menu" abre o painel correspondente
      this.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('[data-nk-dropdown]');
        if (item) this.openDropdown(item);
      });
      this.addEventListener('shopify:block:deselect', () => this.closeDropdowns());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onMediaChange);
      if (this.isDrawerOpen()) document.body.classList.remove('overflow-hidden');
    }

    canHover() {
      return this.desktopQuery.matches && this.hoverQuery.matches;
    }

    /* ---------------------------------------------------------------- Mega menu */
    setupDropdown(item) {
      const trigger = item.querySelector('[data-nk-trigger]');
      if (!trigger) return;

      item.addEventListener('mouseenter', () => {
        if (!this.canHover()) return;
        this.clearCloseTimer();
        this.openDropdown(item);
      });

      item.addEventListener('mouseleave', () => {
        if (!this.canHover()) return;
        this.scheduleClose(item);
      });

      item.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !item.contains(event.relatedTarget)) this.closeDropdown(item);
      });

      trigger.addEventListener('click', (event) => {
        const isKeyboard = event.detail === 0;
        if (this.openItem === item) {
          // No mouse o hover já abriu: o clique só fecha via teclado ou toque
          if (isKeyboard || !this.canHover()) this.closeDropdown(item);
          return;
        }
        this.openDropdown(item);
      });
    }

    openDropdown(item) {
      if (this.openItem === item) return;
      this.closeSearch();
      if (this.openItem) this.closeDropdown(this.openItem, true);

      const trigger = item.querySelector('[data-nk-trigger]');
      const panel = item.querySelector('[data-nk-panel]');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.classList.add('tp-nk-header__nav-link--open');
      if (panel) panel.classList.add('tp-nk-header__panel--open');
      this.openItem = item;
      this.updateOverlay();
    }

    closeDropdown(item, keepOverlay = false) {
      if (!item) return;
      const trigger = item.querySelector('[data-nk-trigger]');
      const panel = item.querySelector('[data-nk-panel]');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove('tp-nk-header__nav-link--open');
      if (panel) panel.classList.remove('tp-nk-header__panel--open');
      if (this.openItem === item) this.openItem = null;
      if (!keepOverlay) this.updateOverlay();
    }

    closeDropdowns() {
      this.clearCloseTimer();
      if (this.openItem) this.closeDropdown(this.openItem);
    }

    scheduleClose(item) {
      this.clearCloseTimer();
      this.closeTimer = window.setTimeout(() => this.closeDropdown(item), 150);
    }

    clearCloseTimer() {
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    /* ---------------------------------------------------------------- Busca */
    isSearchOpen() {
      return Boolean(this.search && this.search.classList.contains('tp-nk-header__search--open'));
    }

    openSearch() {
      if (!this.search) return;
      this.closeDropdowns();
      this.search.classList.add('tp-nk-header__search--open');
      this.searchToggles.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.updateOverlay();
      const input = this.search.querySelector('[data-nk-search-input]');
      if (input) window.setTimeout(() => input.focus(), 50);
    }

    closeSearch(returnFocus = false) {
      if (!this.isSearchOpen()) return;
      this.search.classList.remove('tp-nk-header__search--open');
      this.searchToggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.updateOverlay();
      if (returnFocus && this.searchToggles[0]) this.searchToggles[0].focus();
    }

    /* ---------------------------------------------------------------- Menu mobile */
    isDrawerOpen() {
      return Boolean(this.drawer && this.drawer.classList.contains('tp-nk-header__drawer--open'));
    }

    openDrawer() {
      if (!this.drawer) return;
      this.closeSearch();
      this.drawer.classList.add('tp-nk-header__drawer--open');
      if (this.drawerOpenButton) this.drawerOpenButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      const closeButton = this.drawer.querySelector('[data-nk-drawer-close]');
      window.setTimeout(() => {
        if (typeof trapFocus === 'function') {
          trapFocus(this.drawer, closeButton || this.drawer);
        } else if (closeButton) {
          closeButton.focus();
        }
      }, 50);
    }

    closeDrawer() {
      if (!this.isDrawerOpen()) return;
      this.drawer.classList.remove('tp-nk-header__drawer--open');
      if (this.drawerOpenButton) this.drawerOpenButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.drawerOpenButton);
      } else if (this.drawerOpenButton) {
        this.drawerOpenButton.focus();
      }
    }

    toggleAccordion(button) {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
      const chevron = button.querySelector('.tp-nk-header__drawer-chevron');
      if (chevron) chevron.classList.toggle('tp-nk-header__drawer-chevron--open', !expanded);
    }

    /* ---------------------------------------------------------------- Geral */
    updateOverlay() {
      if (!this.overlay) return;
      const visible = Boolean(this.openItem) || this.isSearchOpen();
      this.overlay.classList.toggle('tp-nk-header__overlay--visible', visible);
    }

    closeAll() {
      this.closeDropdowns();
      this.closeSearch();
      this.closeDrawer();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.isDrawerOpen()) {
        this.closeDrawer();
        return;
      }
      if (this.isSearchOpen()) {
        this.closeSearch(true);
        return;
      }
      if (this.openItem) {
        const trigger = this.openItem.querySelector('[data-nk-trigger]');
        this.closeDropdowns();
        if (trigger) trigger.focus();
      }
    }

    onDocumentClick(event) {
      if (this.openItem && !this.openItem.contains(event.target)) this.closeDropdowns();
      if (this.isSearchOpen() && !this.search.contains(event.target) && !event.target.closest('[data-nk-search-toggle]')) {
        this.closeSearch();
      }
    }
  }

  customElements.define('nk-header', NkHeader);
}
